import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';

import { FileType } from '../../Applicant.types';

import { ReviewTeam } from './conditionTemplates';

export interface ConditionNote {
    id: string;
    conditionId: string;
    note: string;
    createdBy: string;
    dateCreated: string;
    isResolutionNote: boolean;
    isSystemGenerated: boolean;
}

/** Mirrors the backend ConditionResolutionMethod enum (serialized as string). */
export enum ConditionResolutionMethod {
    Unknown = 'Unknown',
    Manual = 'Manual',
    AcceptedAutoResolution = 'AcceptedAutoResolution',
}

export enum ConditionResolutionStatus {
    Unknown = 0,
    Approved = 1,
    Problematic = 2,
}

export interface ConditionFile {
    id: string;
    filename: string;
    type: FileType;
    uploadDate: string;
    uploadedBy: string | null;
}

export interface ConditionWatcher {
    orpUserProfileId: string;
    email: string;
    displayName: string | null;
    avatarUrl: string | null;
    dateCreated: string;
}

export interface Condition {
    id: string;
    applicantId: string;
    description: string;
    products: ProductNames[];
    /** Products whose approval this condition gates while open. Empty = advisory only. */
    blocksApproval: ProductNames[];
    /** Position in Underwriting's fixed sort order (ORIG-1191). Lower sorts first. */
    sortRank: number;
    /** Vendor sources still outstanding for this condition's DDD data. Null = nothing outstanding. */
    dddOutstandingCount?: number | null;
    /** Total vendor sources expected. Null iff dddOutstandingCount is null. */
    dddExpectedCount?: number | null;
    createdBy: string;
    dateCreated: string;
    resolved: boolean;
    resolutionStatus?: ConditionResolutionStatus | null;
    reviewTeams?: ReviewTeam[];
    dateResolved: string | null;
    resolvedBy: string | null;
    resolutionMethod?: ConditionResolutionMethod | null;
    isDeleted?: boolean;
    dateDeleted?: string | null;
    modifiedBy?: string | null;
    editedBy?: string | null;
    dateEdited?: string | null;
    /** Set when this condition was spawned from a Problematic resolution. */
    parentConditionId?: string | null;
    childConditionIds?: string[];
    canResolve?: boolean;
    canDelete?: boolean;
    canEdit?: boolean;
    notes: ConditionNote[];
    files: ConditionFile[];
    watchers?: ConditionWatcher[];
    isWatching?: boolean;
}

export interface AddConditionNoteRequest {
    applicantId: string;
    conditionId: string;
    note: string;
    isResolutionNote?: boolean;
}

export interface ResolveConditionRequest {
    applicantId: string;
    conditionId: string;
    note: string;
    resolutionMethod?: ConditionResolutionMethod;
    resolutionStatus?: ConditionResolutionStatus;
}

/** Options passed up through the onResolve callback path when opening the Resolve modal. */
export interface ResolveConditionOptions {
    initialNote?: string;
    resolutionMethod?: ConditionResolutionMethod;
}

export interface DeleteConditionRequest {
    applicantId: string;
    conditionId: string;
    note: string;
}

export interface EditConditionRequest {
    applicantId: string;
    conditionId: string;
    description: string;
    products: ProductNames[];
    /** Sent explicitly on every edit -- an empty list removes every team. */
    teams: ReviewTeam[];
}

export interface AttachConditionFileRequest {
    applicantId: string;
    conditionId: string;
    fileId: string;
}

export interface ConditionWatcherRequest {
    applicantId: string;
    conditionId: string;
}
